(ns ductile.workflow.parking-data
  (:require [clojure.set :as set]))

(defn row-range [row-ranges]
  (cond (every? number? row-ranges)
        (range (first row-ranges)
               (inc (second row-ranges)))
        (every? vector? row-ranges)
        (mapcat (fn [[start end]] (range start (inc end))) row-ranges)

        :else
        (throw (ex-info "Unexpected row-range format" {:data row-ranges}))))

#_(row-range [1 3])
#_(row-range [[1 3] [5 6]])

(defn rectangle [{:keys [rows columns]}]
  (reduce (fn [acc row] (assoc acc row (into [] (range (first columns) (inc (second columns))))))
          {}
          (range (first rows) (inc (second rows)))))

#_(rectangle {:rows [1 3] :columns [1 5]})

(defn single-row [row columns]
  {row (into [] (range (first columns) (inc (second columns))))})

#_(single-row 5 [1 10])

(defn single-column [rows column]
  (reduce (fn [acc row] (assoc acc row [column]))
          {}
          (range (first rows) (inc (second rows)))))

#_(single-column [1 10] 5)

(defn blocking [row->spots row->blocked-spots]
  (assert (map? row->spots) "input must be a map")
  (assert (map? row->blocked-spots) "input must be a map")
  (reduce (fn [acc [row spots]]
            (if-let [available-spots (seq (set/difference
                                           (into #{} spots)
                                           (into #{} (get row->blocked-spots row))))]
              (assoc acc row (into [] (sort available-spots)))
              acc))
          {}
          row->spots))

#_(blocking (rectangle {:rows [1 2] :columns [1 2]})
            {1 [1] 2 [2]})

(defn join [& row->spots-maps]
  (assert (every? map? row->spots-maps) "all arguments must be maps")
  (reduce (fn [acc row->spots] (merge-with (comp vec sort distinct concat) acc row->spots))
          (first row->spots-maps)
          (rest row->spots-maps)))

(comment
  (join (single-row 1 [1 2]) (single-row 1 [1 3]) {5 [1]})
  (join (single-row 1 [1 2]) (single-column [1 3] 3)))


(def zone->dimensions
  "Used to generate parking entity seed data and specify parking behavior.

  `:assignment-type` set to:
  - `:zone` means only the zone needs to be selected
  - `:row` means the zone and row need to be selected

  Can be partially computed via ductile.insights.compound-parking/parking-dimensions"
  {"Ravenna" {"A" {:assignment-type :row
                   :spots (single-column [1 96] 1)}
              "B" {:assignment-type :row
                   :spots (single-column [1 96] 1)}
              "C" {:assignment-type :row
                   :spots (single-column [1 96] 1)}
              "Comparto C" {:spots (single-column [1 51] 1)}
              "D" {:assignment-type :row
                   :spots (single-column [1 96] 1)}
              "FP" {:spots (join (single-column [0 61] 1)
                                 (single-column [99 99] 1))}
              "FP Perimetro" {:assignment-type :row
                              :spots (single-column [1 50] 1)}
              "FPR" {:assignment-type :zone
                     :spots {1 [1]}
                     :disallow-manual-parking? true}
              "Lucidatura" {:assignment-type :row
                            :spots (single-column [1 5] 1)}
              "Carrozzeria" {:assignment-type :row
                             :spots (single-column [1 5] 1)}
              "Ricarica" {:assignment-type :row
                          :spots (single-column [1 5] 1)}
              "LAVAGGIO" {:assignment-type :zone
                          :spots {1 [1]}}
              "LON" {:assignment-type :zone
                     :spots {1 [1]}}
              "Parco Bisarche" {:assignment-type :row
                                :spots (single-column [1 26] 1)}
              "PDI" {:assignment-type :zone
                     :spots {1 [1]}}
              "GZ" {:assignment-type :row
                    :spots (join (single-column [1 48] 1)
                                 (single-column [101 132] 1))}

              "PB" {:assignment-type :row
                    :spots (single-column [1 50] 1)} ;(perimeter of long quay row 1 to 50)
              "PL" {:assignment-type :row
                    :spots (single-column [1 50] 1)} ;(perimeter of long washing row 1 to 50)
              "PT" {:assignment-type :row
                    :spots (single-column [1 50] 1)} ;(perimeter of long Trattaroli row 1 to 50)
              "ZC" {:assignment-type :row
                    :spots (single-column [1 30] 1)} ;(central area from 1 to 30)
              "XA" {:assignment-type :row
                    :spots (join (single-column [1 62] 1)
                                 (single-column [99 99] 1))} ;from row 1 to row 62 (each row has 5 boxes)
              "X Centrale" {:assignment-type :row
                            :spots (single-column [1 50] 1)}
              "XB" {:assignment-type :row
                    :spots (single-column [1 63] 1)} ;from row 1 to row 63 (each row has 5 boxes)
              }

   "Civitavecchia" {"FPR" {:assignment-type :zone
                           :spots {1 [1]}
                           :disallow-manual-parking? true}
                    "Porto A" {:spots (single-column [1 100] 1)}
                    "Porto B" {:spots (single-column [1 100] 1)}
                    "Porto C" {:spots (single-column [1 100] 1)}
                    "Porto D" {:spots (single-column [1 100] 1)}
                    "Buffer Treno" {:assignment-type :row :spots {1 [1]}}
                    "A" {:spots (single-column [1 5] 1)}
                    "B" {:spots (single-column [1 5] 1)}
                    "C" {:spots (single-column [1 5] 1)}
                    "D" {:spots (single-column [1 5] 1)}
                    "F" {:spots (rectangle {:rows [1 3] :columns [1 100]})}
                    "M" {:spots (rectangle {:rows [1 3] :columns [1 100]})}
                    "N" {:spots (rectangle {:rows [1 3] :columns [1 100]})}
                    "T" {:spots (rectangle {:rows [1 7] :columns [1 100]})}
                    "X" {:spots (single-column [1 5] 1)}
                    "P" {:spots (single-column [1 5] 1)}
                    "CB" {:spots (single-column [1 5] 1)}
                    "CC" {:spots (single-column [1 5] 1)}
                    "CD" {:spots (single-column [1 5] 1)}
                    "CE" {:spots (single-column [1 5] 1)}
                    "CF" {:spots (single-row 1 [1 100])}
                    "BF" {:spots (single-column [1 5] 1)}
                    "PDI" {:spots (single-column [1 5] 1)}
                    "LAV" {:spots (single-column [1 5] 1)}
                    "FN" {:spots (rectangle {:rows [1 8] :columns [1 100]})} ;; temporary for fiumicino
                    "GZ" {:assignment-type :row
                          :spots (single-column [1 16] 1)}
                    "FIU" {:assignment-type :row ;; temporary for fiumicino
                           :spots (single-column [1 199] 1)}
                    "FC" {:assignment-type :row
                          :spots (single-column [1 10] 1)}
                    "SIC" {:assignment-type :row
                           :spots (single-column [1 9] 1)}
                    "OLB" {:assignment-type :row
                           :spots (single-column [1 9] 1)}
                    "CAG" {:assignment-type :row
                           :spots (single-column [1 9] 1)}
                    "BRC" {:assignment-type :row
                           :spots (single-column [1 3] 1)}
                    "PRE" {:assignment-type :row ;; not real lines, but helping for organizing work in the terminal
                           :spots (single-column [1 100] 1)}}
   "Fiumicino" {"GZ" {:assignment-type :row
                      :spots (single-column [1 199] 1)}}
   "Lonato" {;; original boxed zones
             "A" {:spots (single-column [1 16] 1)}
             "B" {:spots (single-column [1 7] 1)}
             "C" {:spots (single-column [1 28] 1)}
             "D" {:spots (single-column [2 25] 1)}
             "E" {:spots (single-column [1 17] 1)}
             "F" {:spots (single-column [1 38] 1)}
             "L" {:spots (single-column [1 32] 1)}
             "LC" {:spots (single-column [1 32] 1)}
             "P" {:spots (single-column [1 5] 1)}
             "T" {:spots (single-column [1 3] 1)}
             "M" {:spots (single-column [1 12] 1)} ;; really row 1-2 with 24 spots, 3-12 with 44 spots

             ;; irregular zones we treat as regular
             "AC" {:spots (single-column [1 16] 1)} ;; really columns range 5-25
             "BINARIO" {:spots (single-column [1 2] 1)} ;; really columns range 50-200
             "DC" {:spots (single-column [3 20] 1)} ;; really columns range 25-34
             "FP" {:spots (single-column [1 3] 1)} ;; really columns range 100-200

             ;; zones with row gaps
             "CAST" {:spots (join (single-column [1 38] 1)
                                  (single-column [189 243] 1))}
             "CC" {:spots (join (single-column [1 20] 1)
                                {26 [1]})} ;; really columns range 10-40
             "EC" {:spots (join (single-column [1 3] 1)
                                {7 [1]})} ;; really columns range 20-40
             "GZ" {:assignment-type :row
                   :spots (join (reduce (fn [acc cols] (join acc (single-column cols 1)))
                                        {}
                                        [[1 42] [44 76] [91 97] [101 165] [201 264] [301 330]])
                                (reduce (fn [acc row] (assoc acc row [1]))
                                        {}
                                        [350 360 370 380 390 400 450 500 550
                                         600 650 700 750 800 850 900 950 997
                                         998 999]))}
             ;; normal spot definition

             "Buffer Treno" {:assignment-type :row
                             :spots {1 [1]}}

             "BRC" {:assignment-type :row
                    :spots (single-column [1 8] 1)}

             "N" {:assignment-type :row
                  :spots (single-column [1 168] 1)}

             "NP" {:spots {1 [1]}}

             "BC" {:spots (single-column [1 7] 1)}
             "CAR-OUT" {:assignment-type :zone
                        :spots {1 [1]}}
             "CARINTERNA" {:assignment-type :zone
                           :spots {1 [1]}}
             "DRIZZONA" {:spots {1 [1]}}
             "FC" {:spots (single-column [1 38] 1)}
             "LAVAGGIO" {:assignment-type :zone
                         :spots {1 [1]}}
             "RICARICA" {:assignment-type :zone
                         :spots {1 [1]}}
             "LP" {:spots {1 [1]}}
             "MEC-IN1" {:assignment-type :zone
                        :spots {1 [1]}}
             "MEC-OUT1" {:assignment-type :zone
                         :spots {1 [1]}}
             "MOSIO" {:spots {1 [1]}}
             "MP" {:spots {1 [1]}}
             "OFFICINA" {:assignment-type :zone
                         :spots {1 [1]}}
             "PDI" {:assignment-type :zone
                    :spots {1 [1]}}
             "POSIZIONE" {:assignment-type :zone
                          :spots {1 [1]}}
             "RD" {:spots {1 [1]}}
             "SAOTTINI" {:assignment-type :zone
                         :spots {1 [1]}}
             "UFFICIO" {:assignment-type :zone
                        :spots {1 [1]}}}

   "Eitting" {; TODO: though we have the amount of parking spots we leave the :assignment-type :row
              ;  there will be a new layout after reconstruction work is done. issue #5915
              "zum LKW" {:assignment-type :row :spots (single-column [1 10] 1)}
              "Baum" {:assignment-type :row :spots (single-column [1 12] 1)}
              "WH" {:assignment-type :row :spots (single-column [1 3] 1)}
              "DS vorne" {:assignment-type :row :spots (single-column [1 6] 1)}
              "DS hinten" {:assignment-type :row :spots (single-column [1 6] 1)}
              "SP7 vorne" {:assignment-type :row :spots (single-column [1 3] 1)}
              "SP7 hinten" {:assignment-type :row :spots (single-column [1 3] 1)}
              "Vorstellspur" {:assignment-type :row :spots {1 [1]}}
              "VO" {:assignment-type :row :spots (single-column [1 6] 1)}
              "A-Si" {:assignment-type :row :spots (single-column [1 28] 1)}
              "Puffer Gutachter" {:assignment-type :row :spots (single-column [1 6] 1)}
              "HR" {:assignment-type :row :spots {1 [1]}}
              "Arval-Eingang 1-3" {:assignment-type :zone :spots {1 [1]}}
              "Mitarbeiter" {:spots (single-row 1 [1 7])}
              "Pforte" {:spots (single-column [1 10] 1) :assignment-type :row}
              "SP2" {:spots (single-row 1 [1 6])}
              "LAGER Z_C" {:spots (single-row 1 [1 26])
                           :parking-type :longterm-storage}
              "LAGER Z_V" {:spots (single-row 1 [1 13])}
              "SP1" {:spots (single-row 1 [1 12])} ; TODO  this will be areas for battery charging
              "SP6" {:spots (single-row 1 [1 12])} ;  TODO  this will be areas for battery charging
              "ÖH" {:spots (single-row 1 [1 12])}
              "Waschhalle" {:spots (single-row 1 [1 3])}
              "Werkstatt" {:spots (single-row 1 [1 50])}
              "Gutachter" {:spots (single-row 1 [1 10])} ; TODO Block 5-10? - no blocki now
              "Quarantäne SP3" {:spots (single-row 1 [1 6])}
              "Quarantäne SP4" {:spots (single-row 1 [1 6])}
              "Quarantäne SP5" {:spots (single-row 1 [1 6])}
              "Lager Z_A" {:spots (conj
                                   (sorted-map)
                                   (join
                                    (rectangle {:rows [1 43] :columns [1 2]})
                                    (single-column [44 113] 1)))
                           :parking-type :longterm-storage}
              "VH" {:spots (rectangle {:rows [1 9] :columns [1 8]})}
              "HH" {:spots (conj
                            (sorted-map)
                            (blocking
                             (join
                              (rectangle {:rows [1 15] :columns [1 5]})
                              (single-row 17 [1 4]) ; spots added by request https://helpdesk.ars-altmann.de/Ticket/228549
                              #_(single-row 1 [5 6]) ; spots exists but are blocked anyway now
                              #_(single-row 2 [5 7]) ; spots exists but are blocked anyway now
                              #_(rectangle {:rows [3 10] :columns [5 8]}) ; spots exists but are blocked anyway now
                              )
                             (join
                              {1 [5]}
                              {3 [5]}
                              (single-column [5 15] 5))))}
              "HH_FB" {:spots (rectangle {:rows [1 5] :columns [1 2]})}
              "HH_FB Puffer" {:spots (single-row 1 [1 2])}
              "S" {:spots (conj
                           (sorted-map)
                           (join
                            (single-row 1 [1 48])
                            (single-row 1 [101 116])))}
              "SH" {:spots (conj
                            (sorted-map)
                            (join
                             (single-row 1 [1 48])
                             (single-row 1 [201 216])))}
              "Lager E" {:spots
                         (conj
                          (sorted-map)
                          (mapcat (fn [row-number] (single-row row-number [1 9])) (range 1 (inc 83) 2))
                          (mapcat (fn [row-number] (single-row row-number [1 8])) (range 2 (inc 84) 2)))
                         :parking-type :longterm-storage}
              "Lager C" {:spots (conj
                                 (sorted-map)
                                 (join
                                  (single-row 1 [1 23])
                                  (rectangle {:rows [2 7] :columns [1 24]})
                                  (rectangle {:rows [8 23] :columns [1 23]})
                                  (rectangle {:rows [24 42] :columns [1 25]})
                                  (rectangle {:rows [43 47] :columns [1 26]})
                                  (rectangle {:rows [48 51] :columns [1 25]})
                                  (single-row 52 [1 27])
                                  ; define Zwischenreihe here:
                                  (single-row 1 [101 107])
                                  (single-row 2 [201 207])
                                  (single-row 3 [101 108])
                                  (single-row 4 [201 208])
                                  (single-row 5 [101 108])
                                  (apply merge
                                         (concat (map (fn [row-number] (single-row row-number [101 107])) (range 7 (inc 17) 2))
                                                 (map (fn [row-number] (single-row row-number [201 207])) (range 8 (inc 16) 2))
                                                 (map (fn [row-number] (single-row row-number [101 107])) (range 19 (inc 23) 2)) ; no Zwischenreihe in 18
                                                 (map (fn [row-number] (single-row row-number [201 207])) (range 20 (inc 24) 2))
                                                 (map (fn [row-number] (single-row row-number [101 108])) (range 25 (inc 51) 2))
                                                 (map (fn [row-number] (single-row row-number [201 208])) (range 26 (inc 52) 2))))))
                         :parking-type :longterm-storage}
              "Lager B" {:spots (conj
                                 (sorted-map)
                                 (blocking (join
                                            #_(single-row 1 [1 23])
                                            (rectangle {:rows [1 2] :columns [1 19]})
                                            (rectangle {:rows [3 10] :columns [1 18]})
                                            (rectangle {:rows [11 24] :columns [1 19]})
                                            (single-row 25 [1 10])
                                            (single-row 26 [1 15])
                                            (single-row 27 [1 21])
                                            ; define Zwischenreihe here:
                                            (apply merge
                                                   (concat (map (fn [row-number] (single-row row-number [101 106])) (range 2 (inc 22) 2))
                                                           (map (fn [row-number] (single-row row-number [201 206])) (range 3 (inc 23) 2))))
                                            (single-row 26 [101 106])
                                            (single-row 27 [201 206]))
                                           (join
                                            {1 [1 19]}
                                            {7 [7]}
                                            (rectangle {:rows [10 11] :columns [1 5]})
                                            (single-row 15 [7 8])
                                            {10 [106]}
                                            {11 [206]})))
                         :parking-type :longterm-storage}
              "Lager B ZR" {:assignment-type :row
                            :spots (apply merge (map (fn [row-number] {row-number [1]}) (range 2 (inc 26) 2)))}
              "Lager A" {:spots (conj
                                 (sorted-map)
                                 (blocking (join
                                            #_(single-row 1 [1 23])
                                            (rectangle {:rows [11 12] :columns [1 12]})
                                            (rectangle {:rows [13 14] :columns [1 14]})
                                            (rectangle {:rows [15 26] :columns [1 13]})
                                            (single-row 27 [1 6])
                                            ; define Zwischenreihe here:
                                            (apply merge
                                                   (concat (map (fn [row-number] (single-row row-number [101 104])) (range 12 (inc 26) 2))
                                                           (map (fn [row-number] (single-row row-number [201 204])) (range 13 (inc 27) 2)))))
                                           (join
                                            (rectangle {:rows [24 25] :columns [100 205]}))))
                         :parking-type :longterm-storage}
              "Ladereihe A26" {:assignment-type :zone :spots {1 [1]}} ; https://helpdesk.ars-altmann.de/Ticket/220370
              "Ladereihe A24" {:assignment-type :zone :spots {1 [1]}}} ; https://helpdesk.ars-altmann.de/Ticket/220370
   "Knautnaundorf" {;  Guten morgen, können wir bitte das Laherlayout anpassen? Spich nicht vorhandene Plätze löschen?
                    ;  Lager 3 Reihe 32 hat 48 Stellplätze
                    ;  Lager 3 Reihe 33,34,35,36,37,38,39 haben 26 Stellplätze.
                    ;  Lager 3 Reihe 40 und 41 haben 24 Stellplätze.
                    ;  Lager 3 Reihe 42 hat 22 Stellplätze
                    ;"Lager 3" {:rows [32 42] :columns [1 47]}
                    "Lager 3" {:spots (blocking (rectangle {:rows [32 42] :columns [1 48]})
                                                (join (rectangle {:rows [33 39] :columns [27 49]})
                                                      (single-row 40 [25 49])
                                                      (single-row 41 [25 49])
                                                      (single-row 42 [23 49])))}
                    "FZ_EIN_LKW" {:assignment-type :row :spots (single-column [1 9] 1)}
                    "Gleis" {:assignment-type :row :spots (single-column [1 3] 1)}
                    "Zelt" {:assignment-type :row :spots (single-column [1 2] 1)}
                    "Teich" {:assignment-type :row :spots (single-column [1 2] 1)}}
   "Krefeld" {"A" {:spots (conj (sorted-map)
                                (blocking (conj ;(sorted-map)
                                           (rectangle {:rows [6 34] :columns [2 30]})
                                           (rectangle {:rows [35 42] :columns [1 30]})
                                           (rectangle {:rows [43 51] :columns [2 36]})
                                           (rectangle {:rows [52 71] :columns [1 36]}))
                                          ; blocking different areas:
                                          (conj ;(sorted-map)
                                           {6 [2 3 9 22 30]
                                            7 [9 23]
                                            16 [16]
                                            17 [16]
                                            19 [2 3 29 30]
                                            20 [2 3 29 30]
                                            32 [2]
                                            33 [2 3 4 27 28 29 30]
                                            34 [2 3 4 5 6 16 26 27 28 29 30]
                                            35 [16]
                                            36 [16 25 28]}
                                           (join {37 [16]} (single-row 37 [23 30]))
                                           {38 [25 28]
                                            40 [16]
                                            41 [16]
                                            42 [16]}
                                           (join {43 [17]} (single-row 43 [32 36]))
                                           (join {44 [17]} (single-row 44 [32 36]))
                                           (join {45 [18]} (single-row 45 [33 36]))
                                           (join {46 [19]} (single-row 46 [34 36]))
                                           (join {47 [19 32 33]} (single-row 47 [32 36]))
                                           (join {48 [20 33 34]} (single-row 48 [33 36]))
                                           (join {49 [21]} (single-row 49 [31 36]))
                                           (join {50 [22]} (single-row 50 [29 36]))
                                           (join {51 [22]} (single-row 51 [29 36]))
                                           {52 [31 32]
                                            53 [1 29 30 31 32]}
                                           (single-row 54 [35 36])
                                           (single-row 56 [35 36])
                                           (rectangle {:rows [58 61] :columns [35 36]})
                                           (single-row 51 [29 36])
                                           (join {62 [1]} (single-row 62 [33 36]))
                                           (join {63 [1]} (single-row 63 [34 36]))
                                           (join {64 [1]} (single-row 64 [33 36]))
                                           (join {65 [1 2]} (single-row 65 [29 36])))))}
              "B" {:spots (single-row 1 [1 10])}    ; storage for buses
              "Bahn" {:assignment-type :row :spots (single-column [1 2] 1)}
              "BS1" {:spots (single-row 1 [1 125])} ; storage overflow
              "BS2" {:spots (single-row 1 [1 125])} ; changed to 125 per helpdesk issue https://helpdesk.ars-altmann.de/Ticket/216244
              "BÜRO" {:spots (single-row 1 [1 30])}
              "E-LADESTAT" {:spots (blocking (rectangle {:rows [1 10] :columns [1 2]})
                                             (single-column [5 10] 2))}
              "EXTERN" {:spots (single-row 1 [1 10])}
              "FZ-EIN_BAH" {:spots (rectangle {:rows [18 30] :columns [1 30]})}
              "FZ-EIN_LKW" {:spots (rectangle {:rows [1 30] :columns [1 10]})
                            :assignment-type :row}
              "FZ-WASCHEN" {:spots (single-row 1 [1 20])}
              "FZ_AUS_LKW" {:spots (rectangle {:rows [1 45] :columns [1 9]})
                            :assignment-type :row}
              "FZ_AUS_SA1" {:spots (rectangle {:rows [1 65] :columns [1 9]})} ; vehicle/load provision
              "ACHSE" {:spots (single-row 1 [1 9])} ; own-wheels parking in front of the office
              "GUTACHTER" {:spots (single-row 1 [1 30])}
              "HH" {:spots (single-row 1 [1 30])}
              "HV-PUFFER" {:spots (single-row 1 [1 50])}
              "K_A-WASCH1" {:spots (rectangle {:rows [1 8] :columns [1 10]})}
              "Puffer Waschhalle" {:spots (single-row 1 [1 15])
                                   :assignment-type :zone}
              "Puffer Werkstatt" {:spots (single-row 1 [1 50])
                                  :assignment-type :zone}
              "LADESTAT1" {:spots (single-row 1 [1 50])}
              "MAP" {:spots (blocking (single-row 1 [1 55])
                                      (single-row 1 [1 3]))}
              "MAST" {:spots (rectangle {:rows [1 6] :columns [1 8]})}
              "POST1" #_{:spots (blocking (single-row 1 [1 90])
                                          (single-row 1 [58 90]))}
              {:spots (single-row 1 [1 57])}
              #_#_"POST2" {:spots (blocking (single-row 1 [1 90])
                                            (join {1 [22 23]}
                                                  (single-row 1 [41 90])
                                                  ;; temporarily blocked
                                                  (single-row 1 [1 40])))}
              "UEBERLAUF" {:spots (single-row 1 [1 1000])}
              "UNFALL" {:spots (single-row 1 [1 20])}
              "Waschhalle" {:spots (single-row 1 [1 15])
                            :assignment-type :zone}
              "Werkstatt" {:spots (single-row 1 [1 60])
                           :assignment-type :zone}
              "ZR" {:spots (blocking (single-row 1 [1 115])
                                     (join (single-row 1 [35 42])
                                           {1 [1]}))}}})

(def all-zones
  (into #{}
        (mapcat keys)
        (vals zone->dimensions)))

(def compound->zone->rows
  (update-vals zone->dimensions
               (fn [zone->dimensions-for-compound]
                 (into {}
                       (map (fn [[zone spots]] [zone (into [] (distinct (map first spots)))]))
                       (update-vals zone->dimensions-for-compound :spots)))))

(defn parking-model [compound]
  (case compound
    ("Lonato" "Civitavecchia" "Ravenna") :parking.assignment/box
    #_#_() :parking.assignment/free-spot
    :parking.assignment/predefined-spot))

(defn assignment-type [{:as params :parking-spot/keys [zone row place]}]
  (if (= "SHIP" zone)
    :row
    (get-in zone->dimensions [(:compound/name params) zone :assignment-type])))
